import {
  INQUIRY_PROCESS_NAME,
  TX_TRANSITION_ACTOR_PROVIDER,
  resolveLatestProcessName,
} from '../transactions/transaction';

const STORAGE_KEY_PREFIX = 'rematch:inbox-read:v1';
const MAX_STORED_TRANSACTIONS = 250;

const entityId = entity => entity?.id?.uuid;
const toMillis = value => {
  const milliseconds = value instanceof Date ? value.getTime() : Date.parse(value);
  return Number.isFinite(milliseconds) ? milliseconds : 0;
};

const latestByCreatedAt = entries =>
  (entries || []).reduce((latest, entry) => {
    return toMillis(entry?.attributes?.createdAt || entry?.createdAt) >
      toMillis(latest?.attributes?.createdAt || latest?.createdAt)
      ? entry
      : latest;
  }, null);

export const getLatestInboxMessage = transaction => {
  const latestMessage = latestByCreatedAt(
    (transaction?.messages || []).filter(message => !message?.attributes?.deleted)
  );
  const messageAt = toMillis(latestMessage?.attributes?.createdAt);

  return messageAt
    ? {
        actorId: entityId(latestMessage?.sender),
        occurredAt: new Date(messageAt).toISOString(),
      }
    : null;
};

const storageKey = userId => `${STORAGE_KEY_PREFIX}:${userId}`;
const readStoredState = userId => {
  if (typeof window === 'undefined' || !userId) {
    return {};
  }
  try {
    return JSON.parse(window.localStorage.getItem(storageKey(userId))) || {};
  } catch (error) {
    return {};
  }
};

export const isInboxTransactionUnread = ({ currentUserId, transaction }) => {
  const transactionId = entityId(transaction);
  const activity = getLatestInboxMessage(transaction);
  if (
    !currentUserId ||
    !transactionId ||
    !activity?.actorId ||
    activity.actorId === currentUserId
  ) {
    return false;
  }

  const readAt = readStoredState(currentUserId)[transactionId];
  return !readAt || toMillis(activity.occurredAt) > toMillis(readAt);
};

export const isInboxTransactionActionable = ({ transaction, transactionRole }) => {
  const listing = transaction?.listing;
  const listingAttributes = listing?.attributes;
  if (listingAttributes?.deleted === true) {
    return false;
  }

  const processName = resolveLatestProcessName(transaction?.attributes?.processName);
  const isProviderInquiry =
    transactionRole === TX_TRANSITION_ACTOR_PROVIDER && processName === INQUIRY_PROCESS_NAME;
  const hasKnownListingState = typeof listingAttributes?.state === 'string';

  return !(isProviderInquiry && hasKnownListingState && listingAttributes.state !== 'published');
};

export const markInboxTransactionRead = ({ currentUserId, transactionId, readAt = new Date() }) => {
  if (typeof window === 'undefined' || !currentUserId || !transactionId) {
    return;
  }
  try {
    const nextState = {
      ...readStoredState(currentUserId),
      [transactionId]: new Date(readAt).toISOString(),
    };
    const trimmedState = Object.fromEntries(
      Object.entries(nextState)
        .sort(([, first], [, second]) => toMillis(second) - toMillis(first))
        .slice(0, MAX_STORED_TRANSACTIONS)
    );
    window.localStorage.setItem(storageKey(currentUserId), JSON.stringify(trimmedState));
  } catch (error) {
    // Inbox remains usable when storage is unavailable or full.
  }
};
